import React, { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { useLang } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { LayoutDashboard, Inbox, FilePen, ClipboardCheck, GraduationCap, CreditCard, Calendar, CalendarRange, Users, UserSquare2, Mail, BarChart3, Settings, LogOut, Languages, Menu, X, Home, BookOpen, Shield, Zap, UserCog, RefreshCw, Banknote } from "lucide-react";

const LOGO_DARK = "/ofa-france-logo-on-light.png";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, key: "dashboard" },
  { to: "/admin/my-courses", icon: BookOpen, key: "myCourses", teacherOnly: true },

  // ─── Admissions ───
  { section: "admissions" },
  { to: "/admin/inquiries", icon: Inbox, key: "inquiries", hideForTeacher: true },
  { to: "/admin/applications", icon: FilePen, key: "applications", hideForTeacher: true },
  { to: "/admin/admissions", icon: ClipboardCheck, key: "admissions", hideForTeacher: true },
  { to: "/admin/enrollments", icon: GraduationCap, key: "enrollments", hideForTeacher: true },
  { to: "/admin/reenrollment", icon: RefreshCw, key: "reenrollment", hideForTeacher: true },
  { to: "/admin/payments", icon: CreditCard, key: "payments", hideForTeacher: true },
  { to: "/admin/sepa", icon: Banknote, key: "sepa", hideForTeacher: true },
  { to: "/admin/pending-payments", icon: Zap, key: "pendingPayments", superOnly: true },

  // ─── Vie scolaire ───
  { section: "studentLife" },
  { to: "/admin/scheduling", icon: Calendar, key: "scheduling" },
  { to: "/admin/grouping", icon: Users, key: "grouping", hideForTeacher: true },
  { to: "/admin/calendar", icon: CalendarRange, key: "calendar" },
  { to: "/admin/families", icon: Users, key: "families", hideForTeacher: true },
  { to: "/admin/students", icon: UserSquare2, key: "students" },
  { to: "/admin/sis", icon: BookOpen, key: "sis" },

  // ─── Système ───
  { section: "system" },
  { to: "/admin/emails", icon: Mail, key: "emails", hideForTeacher: true },
  { to: "/admin/analytics", icon: BarChart3, key: "analytics", hideForTeacher: true },
  { to: "/admin/audit-logs", icon: Shield, key: "auditLogs", hideForTeacher: true },
  { to: "/admin/staff", icon: UserCog, key: "staff", superOnly: true },
  { to: "/admin/settings", icon: Settings, key: "settings" },
];

export default function AdminLayout() {
  const { t, lang, setLang } = useLang();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const SidebarContent = (
    <>
      <div className="p-5 border-b border-border">
        <Link to="/admin" className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
          <img src={LOGO_DARK} alt="OFA France" className="h-10 w-auto" />
          <span className="px-2 py-0.5 rounded-full bg-accent/10 text-accent text-[10px] font-bold tracking-[0.18em] uppercase">Admin</span>
        </Link>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        <Link
          to="/"
          data-testid="admin-back-to-site"
          onClick={() => setMobileOpen(false)}
          className="flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors border border-border text-foreground hover:bg-secondary mb-2"
        >
          <Home className="w-4 h-4" strokeWidth={1.5} />
          <span>{lang === "fr" ? "Retour au site public" : "Back to public site"}</span>
        </Link>
        {NAV.filter(item => {
          if (item.section) return true;  // section headers always rendered (filtered below if empty)
          if (item.superOnly && user?.role !== "super_admin") return false;
          if (item.teacherOnly && user?.role !== "teacher" && user?.role !== "super_admin" && user?.role !== "academic_admin") return false;
          if (item.hideForTeacher && user?.role === "teacher") return false;
          return true;
        }).map((item, idx, arr) => {
          if (item.section) {
            // Hide the section header if no following nav item is visible (all filtered out for this role)
            const nextItems = arr.slice(idx + 1).filter(x => !x.section);
            const nextSectionIdx = arr.findIndex((x, i) => i > idx && x.section);
            const upTo = nextSectionIdx === -1 ? arr.length : nextSectionIdx;
            const sectionItems = arr.slice(idx + 1, upTo);
            if (sectionItems.length === 0) return null;
            return (
              <div key={`s-${item.section}`} className="pt-3 pb-1 px-2 text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground/70">
                {t(`admin.section_${item.section}`)}
              </div>
            );
          }
          const { to, end, icon: Icon, key } = item;
          return (
            <NavLink
              key={key}
              to={to}
              end={end}
              data-testid={`nav-${key}`}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={1.5} />
              <span>{t(`admin.${key}`)}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="flex items-center justify-between mb-2 px-2">
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          <button data-testid="admin-lang-toggle" onClick={() => setLang(lang === "fr" ? "en" : "fr")} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <Languages className="w-3 h-3" />{lang.toUpperCase()}
          </button>
        </div>
        <button data-testid="admin-logout" onClick={() => { logout(); window.location.href = "/"; }} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-secondary text-muted-foreground">
          <LogOut className="w-4 h-4" /> {t("nav.logout")}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background" data-testid="admin-layout">
      {/* Desktop sidebar */}
      <aside className="w-64 shrink-0 border-r border-border bg-card hidden md:flex flex-col">
        {SidebarContent}
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-card flex flex-col shadow-2xl">
            <button onClick={() => setMobileOpen(false)} className="absolute top-3 right-3 p-2 rounded-md hover:bg-secondary" data-testid="mobile-menu-close">
              <X className="w-5 h-5" />
            </button>
            {SidebarContent}
          </aside>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-40 bg-card border-b border-border flex items-center justify-between px-4 h-14">
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-open" className="p-2 -ml-2 rounded-md hover:bg-secondary">
            <Menu className="w-5 h-5" />
          </button>
          <img src={LOGO_DARK} alt="OFA" className="h-7" />
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground" data-testid="mobile-home-link">{lang === "fr" ? "Site public" : "Public site"}</Link>
        </div>
        <div className="p-4 md:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
